import { useMemo } from "react";
import { Car, MessageCircle, Pencil, Plus, RefreshCw } from "lucide-react";
import { daysLeft } from "../lib/finance";
import { waTarget } from "../lib/wa";
import { callCount } from "../lib/renewal";
import { PageTitle, Empty } from "./common";
import { CalendarExportBanner } from "./CalendarExportBanner";

export function Renewals({ upcoming, followUps, setFollowUpPolicy, setModal, customers = [], userName = "", vehicles = [], setEditingVehicle }) {
  const customerPhone = (nameOrId, policy = null) => {
    const target = (nameOrId || "").trim().toLocaleLowerCase("tr");
    let c = target && customers.find((x) => (x.name || "").trim().toLocaleLowerCase("tr") === target);
    if (!c && policy?.customerId) c = customers.find((x) => x.id === policy.customerId);
    return c?.phone || "";
  };
  const upcomingInspections = useMemo(() => {
    return vehicles
      .filter((v) => v.inspectionEnd)
      .map((v) => ({ ...v, dl: daysLeft(v.inspectionEnd) }))
      .filter((v) => v.dl <= 60)
      .sort((a, b) => a.dl - b.dl);
  }, [vehicles]);
  const buildInspectionMsg = (v) => {
    const greet = v.customer ? `Merhaba ${v.customer},` : "Merhaba,";
    const vehicle = v.plate ? `${v.plate} plakalı aracınızın ` : "";
    const signoff = userName ? `\n\nİyi günler,\n${userName}` : "";
    return `${greet}\n\n${vehicle}muayenesinin bitiş tarihi ${v.inspectionEnd}. Randevu almanız için size yardımcı olabilirim.${signoff}`;
  };
  const buildRenewalMsg = (p) => {
    const greet = p.customer ? `Merhaba ${p.customer},` : "Merhaba,";
    const vehicle = p.plate ? `${p.plate} plakalı aracınızın ` : "";
    const branchText = p.branch ? `${p.branch} ` : "";
    const polText = p.policeNo ? `${p.policeNo} numaralı ` : "";
    const signoff = userName ? `\n\nİyi günler,\n${userName}` : "";
    return `${greet}\n\n${vehicle}${branchText}${polText}poliçenizin bitiş tarihi ${p.end}. Yenileme için uygun olduğunuz zamanı yazabilir misiniz?${signoff}`;
  };
  return (
    <>
      <PageTitle eyebrow="ZAMANINDA HATIRLAT" title="Yenilemeler" description="Yaklaşan poliçe bitişlerini takip edin ve arama sonuçlarını kaydedin." />
      <CalendarExportBanner />
      <section className="panel table-panel">
        <div className="renewal-banner">
          <RefreshCw size={18} />
          <div><strong>{upcoming.length} poliçe</strong><span>önümüzdeki 60 gün içinde yenileniyor</span></div>
        </div>
        <div className="renewal-cards">
          {upcoming.map((p) => {
            const wa = waTarget(customerPhone(p.customer, p));
            return (
              <div className="renewal-card" key={p.id} data-testid={`renewal-card-${p.id}`}>
                <div>
                  <strong>{p.customer}{p.plate ? <span className="plate-chip">{p.plate}</span> : null}</strong>
                  <span>{p.branch} · {p.policeNo} · {Math.max(0, daysLeft(p.end))} gün · Bitiş: {p.end}</span>
                  {p.renewalNote && <span className="rt-note has" data-testid={`renewals-note-${p.id}`}>{p.renewalNote}{callCount(p) > 0 ? ` · ${callCount(p)} arama` : ""}</span>}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {wa && (
                    <a className="whatsapp-button" data-testid={`renewal-wa-${p.id}`} href={`https://wa.me/${wa}?text=${encodeURIComponent(buildRenewalMsg(p))}`} target="_blank" rel="noreferrer" title="WhatsApp'tan Gönder">
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                  )}
                  <button className="outline-button" data-testid={`followup-button-${p.id}`} onClick={() => { setFollowUpPolicy(p); setModal("followup"); }}><Plus size={14} /> Takip notu</button>
                </div>
                <div className="followup-history">
                  {followUps.filter((f) => f.policyId === p.id).slice(0, 3).map((f) => (
                    <span key={f.id}>{f.date} · {f.status}{f.note ? ` — ${f.note.slice(0, 40)}` : ""}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {!upcoming.length && <Empty text="Yaklaşan yenileme bulunmuyor" />}
      </section>
      <section className="panel table-panel" data-testid="inspections-section" style={{ marginTop: 24 }}>
        <div className="renewal-banner" style={{ background: "#fef3c7", borderColor: "#fde68a" }}>
          <Car size={18} />
          <div><strong>{upcomingInspections.length} araç</strong><span>önümüzdeki 60 gün içinde muayenesi var</span></div>
        </div>
        <div className="renewal-cards">
          {upcomingInspections.map((v) => {
            const wa = waTarget(customerPhone(v.customer));
            const label = v.dl < 0 ? `${Math.abs(v.dl)} gün gecikti` : `${v.dl} gün kaldı`;
            const sev = v.dl < 0 ? "red" : v.dl <= 30 ? "amber" : "slate";
            return (
              <div className="renewal-card" key={v.id} data-testid={`inspection-card-${v.id}`}>
                <div>
                  <strong>{v.plate}{v.customer ? <span className="muted small" style={{ marginLeft: 8 }}>· {v.customer}</span> : null}</strong>
                  <span>{[v.brand, v.model, v.year].filter(Boolean).join(" ") || "—"} · Son muayene: {v.inspectionLast || "—"} · Bitiş: <strong>{v.inspectionEnd}</strong> · <span className={`badge badge-${sev}`}>{label}</span></span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {wa && (
                    <a className="whatsapp-button" data-testid={`inspection-wa-${v.id}`} href={`https://wa.me/${wa}?text=${encodeURIComponent(buildInspectionMsg(v))}`} target="_blank" rel="noreferrer" title="WhatsApp'tan Gönder">
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                  )}
                  <button className="outline-button" data-testid={`inspection-edit-${v.id}`} onClick={() => { setEditingVehicle(v); setModal("vehicle"); }}><Pencil size={14} /> Güncelle</button>
                </div>
              </div>
            );
          })}
        </div>
        {!upcomingInspections.length && <Empty text="Yaklaşan muayene bulunmuyor" />}
      </section>
    </>
  );
}
